import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, IconButton, Button, Switch, FormControlLabel } from '@mui/material';
import { Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import {
  useGetPaymentConfigQuery,
  useCreatePaymentConfigMutation,
  useUpdatePaymentConfigMutation,
  PaymentConfig,
  MerchantConfigWithFrequency,
} from '@/services/paymentConfigApi';
import { useGetMerchantConfigsByAMCQuery } from '@/services/gatewayConfigApi';
import { FormikTextField } from '../components/FormikTextField';
import { FormikRadioGroup } from '../components/FormikRadioGroup';
import { FormikSubmitButton } from '../components/FormikSubmitButton';
import MerchantConfigModal from '../components/MerchantConfigModal';
import { useState } from 'react';
import { getAggregatorLogo } from '../constants/icons';

const validationSchema = yup.object({
  name: yup.string().required('Name is required'),
  trtype: yup.string().oneOf(['LUMPSUM', 'SIP']).required('Transaction Type is required'),
  paymode: yup
    .string()
    .oneOf(['NET_BANKING', 'UPI', 'DEBIT_CARD'])
    .required('Payment Mode is required'),
  authmode: yup.string().oneOf(['NET_BANKING', 'API']).required('Auth Mode is required'),
  merchantConfigs: yup
    .array()
    .of(
      yup.object({
        id: yup.string().required('Merchant Config is required'),
        frequency: yup
          .number()
          .min(1, 'Frequency must be at least 1')
          .required('Frequency is required'),
      })
    )
    .min(1, 'At least one merchant config is required'),
});

type FormValues = Omit<PaymentConfig, 'id' | 'asset'>;

const initialValues: FormValues = {
  name: '',
  trtype: 'LUMPSUM',
  paymode: 'NET_BANKING',
  authmode: 'NET_BANKING',
  merchantConfigs: [],
};

const TRANSACTION_TYPE_OPTIONS = [
  { value: 'LUMPSUM', label: 'Lumpsum' },
  { value: 'SIP', label: 'SIP' },
];

const PAYMENT_MODE_OPTIONS = [
  { value: 'NET_BANKING', label: 'Net Banking' },
  { value: 'UPI', label: 'UPI' },
  { value: 'DEBIT_CARD', label: 'Debit Card' },
];

const AUTH_MODE_OPTIONS = [
  { value: 'NET_BANKING', label: 'Net Banking' },
  { value: 'API', label: 'API' },
];

export default function PaymentConfigEdit() {
  const { assetId = '', configId } = useParams<{ assetId: string; configId: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const isNew = configId === 'new';
  const [modalOpen, setModalOpen] = useState(false);
  const [isPriorityBased, setIsPriorityBased] = useState(false);
  const [currentMerchantConfigs, setCurrentMerchantConfigs] = useState<
    MerchantConfigWithFrequency[]
  >([]);

  const { data: paymentConfig } = useGetPaymentConfigQuery(configId!, { skip: isNew });
  const { data: merchantConfigs } = useGetMerchantConfigsByAMCQuery(assetId);
  const [createPaymentConfig] = useCreatePaymentConfigMutation();
  const [updatePaymentConfig] = useUpdatePaymentConfigMutation();

  const handleSubmit = async (values: FormValues) => {
    try {
      if (isNew) {
        await createPaymentConfig({
          ...values,
          asset: { id: assetId },
        }).unwrap();
      } else {
        await updatePaymentConfig({
          ...values,
          id: configId!,
          asset: { id: assetId },
        }).unwrap();
      }
      enqueueSnackbar('Payment configuration saved successfully', { variant: 'success' });
      navigate(`/assets/${assetId}/payment-configs`);
    } catch (error: any) {
      enqueueSnackbar(error.data?.message || 'Failed to save payment configuration', {
        variant: 'error',
      });
    }
  };

  const openMerchantConfigModal = (configs: MerchantConfigWithFrequency[]) => {
    setCurrentMerchantConfigs(configs);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const renderMerchantConfigRow = (config: MerchantConfigWithFrequency) => {
    const merchantConfig = merchantConfigs?.find((mc) => mc.id === config.id);
    if (!merchantConfig) return null;

    const logo = getAggregatorLogo(merchantConfig.aggregator || '');

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 1 }} key={config.id}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
          <img src={logo} alt={merchantConfig.aggregator} height="20" />
          <Typography>{merchantConfig.aggregator}</Typography>
        </Box>
        <Typography sx={{ flex: 1 }}>{config.frequency}%</Typography>
        <IconButton size="small" color="primary">
          <EditIcon fontSize="small" />
        </IconButton>
      </Box>
    );
  };

  return (
    <Box p={3}>
      <Typography variant="h5" sx={{ mb: 4 }}>
        {isNew ? 'Create New Payment Config' : 'Edit Payment Config'}
      </Typography>

      <Formik
        initialValues={paymentConfig || initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values }) => (
          <Form>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 600 }}>
              <FormikTextField name="name" label="Name" fullWidth />

              <FormikRadioGroup
                name="trtype"
                label="Transaction Type"
                options={TRANSACTION_TYPE_OPTIONS}
              />

              <FormikRadioGroup
                name="paymode"
                label="Payment Mode"
                options={PAYMENT_MODE_OPTIONS}
              />

              <FormikRadioGroup name="authmode" label="Auth Mode" options={AUTH_MODE_OPTIONS} />

              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                  }}
                >
                  <Typography variant="subtitle1">Aggregator Configurations</Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => openMerchantConfigModal(values.merchantConfigs)}
                  >
                    Manage Aggregators
                  </Button>
                </Box>

                <Box
                  sx={(theme) => ({
                    border: theme.borders.borderReport,
                    borderRadius: 1,
                    p: 2,
                    backgroundColor: theme.palette.background.default,
                  })}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isPriorityBased}
                          onChange={(e) => setIsPriorityBased(e.target.checked)}
                        />
                      }
                      label={isPriorityBased ? 'Priority Based' : 'Percentage Based'}
                    />
                  </Box>

                  {values.merchantConfigs.length > 0 ? (
                    <Box>{values.merchantConfigs.map(renderMerchantConfigRow)}</Box>
                  ) : (
                    <Typography color="text.secondary" align="center" py={2}>
                      No aggregators configured. Click &quot;Manage Aggregators&quot; to add some.
                    </Typography>
                  )}
                </Box>
              </Box>

              <FormikSubmitButton>
                {isNew ? 'Create Payment Config' : 'Update Payment Config'}
              </FormikSubmitButton>
            </Box>
          </Form>
        )}
      </Formik>

      {/* Merchant Config Modal */}
      <MerchantConfigModal
        open={modalOpen}
        gatewaySelectionAlgo={paymentConfig?.gatewaySelectionAlgo || 'Percentage'}
        onClose={handleModalClose}
        paymentConfigId={configId || ''}
        amcId={assetId}
        initialConfigs={currentMerchantConfigs}
      />
    </Box>
  );
}
